import { demoInvestments } from "../data/demoData";
import { isSupabaseConfigured, supabase } from "../lib/supabase";
import type { DataStatus, FinancialPeriod, InvestmentRecord, Recommendation, Zone } from "../types/domain";

export interface DataServiceResult {
  records: InvestmentRecord[];
  source: "supabase" | "demo";
  message: string;
}

const status = (value: unknown): DataStatus => {
  if (value === "approved" || value === "pending" || value === "sample" || value === "stale" || value === "unavailable") return value;
  return "unavailable";
};

const zone = (value: unknown): Zone => {
  if (value === "Gold" || value === "Silver" || value === "Watch" || value === "Fair Value" || value === "Avoid") return value;
  return "Avoid";
};

const recommendation = (value: unknown): Recommendation => {
  if (value === "Strong Buy" || value === "Buy" || value === "Hold" || value === "Wait" || value === "Reduce" || value === "Avoid") return value;
  return "Wait";
};

const latestByCompany = <T extends { company_id: string }>(rows: T[], dateKey: keyof T) => {
  const map = new Map<string, T>();
  rows.forEach((row) => {
    const current = map.get(row.company_id);
    const currentDate = current?.[dateKey] ? String(current[dateKey]) : "";
    const nextDate = row[dateKey] ? String(row[dateKey]) : "";
    if (!current || nextDate > currentDate) map.set(row.company_id, row);
  });
  return map;
};

const mapFinancialPeriod = (row: any): FinancialPeriod => ({
  fiscalYear: row.fiscal_year,
  fiscalQuarter: row.fiscal_quarter ?? undefined,
  periodType: row.period_type,
  periodEndDate: row.period_end_date,
  currency: row.currency,
  units: row.units,
  revenue: row.revenue,
  operatingIncome: row.operating_income,
  netIncome: row.net_income,
  eps: row.eps,
  operatingCashFlow: row.operating_cash_flow,
  capitalExpenditure: row.capital_expenditure,
  freeCashFlow: row.free_cash_flow,
  cashAndEquivalents: row.cash_and_equivalents,
  totalDebt: row.total_debt,
  shareholdersEquity: row.shareholders_equity,
  sharesOutstanding: row.shares_outstanding,
  dividendsPaid: row.dividends_paid,
  interestExpense: row.interest_expense,
  currentAssets: row.current_assets,
  currentLiabilities: row.current_liabilities,
  inventory: row.inventory,
  source: row.source,
  sourceUrl: row.source_url ?? undefined,
  dataStatus: status(row.approval_status)
});

export const loadInvestments = async (): Promise<DataServiceResult> => {
  if (!isSupabaseConfigured || !supabase) {
    return {
      records: demoInvestments,
      source: "demo",
      message: "Supabase is not configured. Showing fictional sample data."
    };
  }

  const [companiesResult, pricesResult, financialsResult, valuationsResult, scoresResult, recommendationsResult] = await Promise.all([
    supabase.from("companies").select("*").eq("active", true).order("ticker"),
    supabase.from("prices").select("*").in("data_status", ["approved", "sample", "stale"]).order("price_date", { ascending: false }),
    supabase.from("financial_periods").select("*").in("approval_status", ["approved", "sample", "stale"]).order("fiscal_year", { ascending: false }),
    supabase.from("valuation_results").select("*").in("data_status", ["approved", "sample", "stale"]).order("calculation_date", { ascending: false }),
    supabase.from("quality_scores").select("*").in("data_status", ["approved", "sample", "stale"]),
    supabase.from("recommendations").select("*").in("data_status", ["approved", "sample", "stale"])
  ]);

  const dailyPricesResult = await supabase
    .from("daily_price_history")
    .select("*")
    .in("data_status", ["approved", "sample", "stale"])
    .order("price_date", { ascending: false });

  const firstError = companiesResult.error ?? pricesResult.error ?? financialsResult.error ?? valuationsResult.error ?? scoresResult.error ?? recommendationsResult.error;
  if (firstError) {
    return {
      records: demoInvestments,
      source: "demo",
      message: `Supabase could not be loaded: ${firstError.message}. Showing sample data.`
    };
  }

  const companies = companiesResult.data ?? [];
  if (!companies.length) {
    return { records: [], source: "supabase", message: "Supabase is connected, but no published companies are available yet." };
  }

  const prices = latestByCompany(pricesResult.data ?? [], "price_date");
  const dailyPrices = latestByCompany(dailyPricesResult.error ? [] : dailyPricesResult.data ?? [], "price_date");
  const valuations = latestByCompany(valuationsResult.data ?? [], "calculation_date");
  const scores = new Map((scoresResult.data ?? []).map((row: any) => [row.company_id, row]));
  const recommendations = new Map((recommendationsResult.data ?? []).map((row: any) => [row.company_id, row]));
  const financialsByCompany = new Map<string, FinancialPeriod[]>();
  (financialsResult.data ?? []).forEach((row: any) => {
    const list = financialsByCompany.get(row.company_id) ?? [];
    list.push(mapFinancialPeriod(row));
    financialsByCompany.set(row.company_id, list);
  });

  const records = companies.map((company: any): InvestmentRecord => {
    const legacyPrice = prices.get(company.id) as any;
    const dailyPrice = dailyPrices.get(company.id) as any;
    const valuation = valuations.get(company.id) as any;
    const quality = scores.get(company.id) as any;
    const rec = recommendations.get(company.id) as any;
    const financials = financialsByCompany.get(company.id) ?? [];
    const latestFinancial = financials[0];
    const currentPrice = dailyPrice?.close ?? legacyPrice?.price ?? null;
    const annualDividendPerShare = latestFinancial?.dividendsPaid && latestFinancial?.sharesOutstanding ? Math.abs(latestFinancial.dividendsPaid) / latestFinancial.sharesOutstanding : null;
    const dividendYield = annualDividendPerShare && currentPrice ? annualDividendPerShare / currentPrice : null;

    return {
      company: {
        id: company.id,
        ticker: company.ticker,
        companyName: company.company_name,
        slug: company.slug,
        market: company.market,
        exchange: company.exchange ?? "",
        nativeCurrency: company.native_currency,
        sector: company.sector,
        industry: company.industry ?? "",
        country: company.country ?? "",
        businessDescription: company.business_description ?? "No approved business description is available.",
        investorRelationsUrl: company.investor_relations_url ?? undefined
      },
      price: {
        price: currentPrice,
        currency: dailyPrice?.currency ?? legacyPrice?.currency ?? company.native_currency,
        priceMxn: dailyPrice?.price_mxn ?? legacyPrice?.price_mxn ?? null,
        fxRate: dailyPrice?.fx_rate ?? legacyPrice?.fx_rate ?? null,
        priceDate: dailyPrice?.price_date ?? legacyPrice?.price_date ?? null,
        open: dailyPrice?.open ?? null,
        high: dailyPrice?.high ?? null,
        low: dailyPrice?.low ?? null,
        volume: dailyPrice?.volume ?? null,
        previousClose: dailyPrice?.previous_close ?? null,
        dailyChange: dailyPrice?.daily_change ?? null,
        dailyChangePercent: dailyPrice?.daily_change_percent ?? null,
        source: dailyPrice?.source ?? legacyPrice?.source ?? "Unavailable",
        sourceUrl: dailyPrice?.source_url ?? legacyPrice?.source_url ?? undefined,
        dataStatus: status(dailyPrice?.data_status ?? legacyPrice?.data_status)
      },
      financials,
      valuation: {
        conservativeValue: valuation?.conservative_value ?? null,
        baseValue: valuation?.base_value ?? null,
        optimisticValue: valuation?.optimistic_value ?? null,
        strongBuyPrice: valuation?.strong_buy_price ?? null,
        buyPrice: valuation?.buy_price ?? null,
        fairValueLow: valuation?.fair_value_low ?? null,
        fairValueHigh: valuation?.fair_value_high ?? null,
        takeProfitPrice: valuation?.take_profit_price ?? null,
        expectedFiveYearPrice: valuation?.expected_five_year_price ?? null,
        expectedFiveYearCagr: valuation?.expected_five_year_cagr ?? null,
        marginOfSafety: valuation?.margin_of_safety ?? null,
        modelVersion: valuation?.model_version ?? "unavailable",
        calculationDate: valuation?.calculation_date ?? "Unavailable",
        dataStatus: status(valuation?.data_status)
      },
      quality: {
        businessQualityScore: quality?.business_quality_score ?? 0,
        financialStrengthScore: quality?.financial_strength_score ?? 0,
        valuationScore: quality?.valuation_score ?? 0,
        growthScore: quality?.growth_score ?? 0,
        managementScore: quality?.management_score ?? 0,
        dividendScore: quality?.dividend_score ?? 0,
        riskScore: quality?.risk_score ?? 0,
        buffettScore: quality?.buffett_score ?? 0,
        opportunityScore: quality?.opportunity_score ?? 0,
        moatNotes: quality?.moat_notes ?? "No approved moat analysis is available.",
        managementNotes: quality?.management_notes ?? "No approved management analysis is available.",
        riskNotes: quality?.risk_notes ?? "No approved risk analysis is available.",
        dataStatus: status(quality?.data_status)
      },
      recommendation: {
        recommendation: recommendation(rec?.recommendation),
        zone: zone(rec?.zone),
        investmentThesis: rec?.investment_thesis ?? "No approved thesis is available.",
        reasonsToBuy: rec?.reasons_to_buy ?? [],
        reasonsNotToBuy: rec?.reasons_not_to_buy ?? [],
        confidence: rec?.confidence ?? 0,
        unresolvedCriticalWarning: rec?.unresolved_critical_warning ?? false,
        dataStatus: status(rec?.data_status),
        updatedAt: rec?.updated_at ?? "Unavailable"
      },
      dividendYield,
      dividendSafety: dividendYield === null ? "Unavailable" : dividendYield > 0.025 ? "High" : "Medium",
      riskRating: (quality?.risk_score ?? 0) >= 4 ? "Low" : (quality?.risk_score ?? 0) >= 2 ? "Medium" : "High"
    };
  });

  return {
    records,
    source: "supabase",
    message: dailyPricesResult.error
      ? "Supabase is connected. Daily price history is not migrated yet, so legacy price snapshots are shown."
      : "Supabase is connected. Showing stored end-of-day market data from the database."
  };
};
